import { useSelector, useDispatch } from 'react-redux'
import {
  setItems,
  addItem,
  updateItem,
  deleteItem,
  selectItem,
  clearSelection,
  setSearchTerm,
  setCategoryFilter,
  setDepartmentFilter,
  setStockStatusFilter,
  setExpiryStatusFilter,
  clearFilters,
  setLoading,
  setError
} from '../../../redux/inventorySlice'
import { generateId } from '../utils/helpers'
import { useState } from 'react'

const useInventory = () => {
  const dispatch = useDispatch()
  const inventory = useSelector((state) => state.inventory.items)
  const filteredItems = useSelector((state) => state.inventory.filteredItems)
  const selectedItem = useSelector((state) => state.inventory.selectedItem)
  const searchTerm = useSelector((state) => state.inventory.searchTerm)
  const categoryFilter = useSelector((state) => state.inventory.categoryFilter)
  const departmentFilter = useSelector((state) => state.inventory.departmentFilter)
  const stockStatusFilter = useSelector((state) => state.inventory.stockStatusFilter)
  const expiryStatusFilter = useSelector((state) => state.inventory.expiryStatusFilter)
  const loading = useSelector((state) => state.inventory.loading)
  const error = useSelector((state) => state.inventory.error)

  // Local state for modal management
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const showAddModal = () => {
    setIsAddModalVisible(true)
  }

  const hideAddModal = () => {
    setIsAddModalVisible(false)
  }

  const showEditModal = (item) => {
    dispatch(selectItem(item))
    setIsEditModalVisible(true)
  }

  const hideEditModal = () => {
    setIsEditModalVisible(false)
  }

  const showDeleteModal = (item) => {
    dispatch(selectItem(item))
    setIsDeleteModalVisible(true)
  }

  const hideDeleteModal = () => {
    setIsDeleteModalVisible(false)
  }

  // CRUD operations
  const addInventoryItem = (item) => {
    try {
      // Generate a new ID if none is provided
      if (!item.id) {
        item.id = generateId(
          'inv-',
          inventory.map((i) => i.id)
        )
      }

      dispatch(addItem(item))
      hideAddModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error adding inventory item'))
      return false
    }
  }

  const updateInventoryItem = (item) => {
    try {
      dispatch(updateItem(item))
      hideEditModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error updating inventory item'))
      return false
    }
  }

  const deleteInventoryItem = (id) => {
    try {
      dispatch(deleteItem(id))
      hideDeleteModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error deleting inventory item'))
      return false
    }
  }

  // Search and filtering
  const handleSearch = (term) => {
    dispatch(setSearchTerm(term))
  }

  const filterByCategory = (category) => {
    dispatch(setCategoryFilter(category))
  }

  const filterByDepartment = (department) => {
    dispatch(setDepartmentFilter(department))
  }

  const filterByStockStatus = (status) => {
    dispatch(setStockStatusFilter(status))
  }

  const filterByExpiryStatus = (status) => {
    dispatch(setExpiryStatusFilter(status))
  }

  const resetFilters = () => {
    dispatch(clearFilters())
  }

  // Selection handling
  const selectInventoryItem = (item) => {
    dispatch(selectItem(item))
  }

  const clearInventorySelection = () => {
    dispatch(clearSelection())
  }

  return {
    // Data
    inventory,
    filteredItems,
    selectedItem,
    searchTerm,
    categoryFilter,
    departmentFilter,
    stockStatusFilter,
    expiryStatusFilter,
    loading,
    error,

    // Modal state and handlers
    isAddModalVisible,
    isEditModalVisible,
    isDeleteModalVisible,
    showAddModal,
    hideAddModal,
    showEditModal,
    hideEditModal,
    showDeleteModal,
    hideDeleteModal,

    // CRUD operations
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,

    // Search and filtering
    handleSearch,
    filterByCategory,
    filterByDepartment,
    filterByStockStatus,
    filterByExpiryStatus,
    resetFilters,

    // Selection handling
    selectInventoryItem,
    clearInventorySelection
  }
}

export default useInventory
